import React, { useState } from 'react'
import { SubformProps, Validation } from "."
import { Applicant, errorToString, LoanApplication, Validate } from '@oneethos/shared'
import ApplicantForm from './applicant-form'
import EmploymentForm from './employment-form'
import { Spinner } from '@fluentui/react'
import { useAppState } from '../../hooks'
import { isPersonaVerificationComplete } from './IdentityVerification'
import { personaVerificationFormPatch } from './util'

// identity verification only needs the fields Persona uses to reach the co-borrower;
// the rest of the step is validated by the Continue button
const CO_IDENTITY_FIELDS = [
  'coFirstName',
  'coLastName',
  'coSsn',
  'coPhone',
  'coEmail',
  'coBirthDate',
]

type CoborrowerFieldsProps = Partial<SubformProps> & {
  onBeforeVerify?: () => Promise<boolean>
  summary?: boolean
}

export const CoborrowerFields = ({
  formData,
  update,
  onBeforeVerify,
  summary,
}: CoborrowerFieldsProps) => {
  const { tenant } = useAppState()
  const handleCoborrowerChange = (data: string | Record<string, unknown>, val?: unknown) => {
    if (typeof data === 'string') {
      const k = 'co' + data[0].toUpperCase() + data.substring(1)
      update(k, val)
    } else {
      const obj = {}
      Object.entries(data).forEach(([k, v]) => {
        obj['co' + k[0].toUpperCase() + k.substring(1)] = v
      })
      update(obj)
    }
  }

  const coborrower = Applicant.fromLoanAppCoBorrower(formData, true)
  const coRequired = (
    formData.prequalResultCombined === 'pass' &&
    formData.prequalResult !== 'pass'
  )

  const coBorrowerIncluded = coRequired || summary || formData.includeCoBorrower === 'yes'
  const usesPersona =
    formData.identityVerificationVersion === 'persona' &&
    !tenant.config?.personaDisabled
  const coBorrowerVerified =
    !usesPersona ||
    isPersonaVerificationComplete(formData.personaVerifications?.coBorrower)

  return <>
    {/*
      we only give them the option to add a coborrower at this step if the primary 
      borrower passed prequal on their own;
      otherwise it should be required if they've made it to this step
    */}
    <h2>Co-Borrower Information</h2>
    {!coRequired ? <>
      <div className="form-group">
        <label>
          Would you like to include a Co-Borrower in your application?
          Including a co-borrower will result in a hard credit inquiry for the co-borrower,
          which can impact their credit score.
        </label>
        <select
          className="form-select"
          value={formData.includeCoBorrower}
          onChange={ev => update('includeCoBorrower', ev.target.value)}
        >
          <option value='no'>No</option>
          <option value='yes'>Yes</option>
        </select>
      </div>
    </> : null}
    {coBorrowerIncluded ? <>
      <ApplicantForm
        applicant={{ ...coborrower }}
        update={handleCoborrowerChange}
        address={formData.coBorrowerAddress || { country: 'United States' }}
        onAddressChange={addr => update('coBorrowerAddress', addr)}
        disableCoreChanges={!!formData.prequalResultCombined}
        party="co-borrower"
        verification={formData.personaVerifications?.coBorrower}
        identityVerificationVersion={formData.identityVerificationVersion}
        loanApplicationId={summary ? undefined : formData._id}
        onVerificationUpdated={summary ? undefined : (lapp: LoanApplication) => {
          update(personaVerificationFormPatch(lapp, 'co-borrower'))
        }}
        onBeforeVerify={onBeforeVerify}
      />
      {coBorrowerVerified || summary ? (
        <>
          <h2>Co-Borrower Employment Information</h2>
          <EmploymentForm
            employment={coborrower}
            onChange={handleCoborrowerChange}
            disableIncomeChanges={!!formData.prequalResultCombined}
          />
        </>
      ) : null}
    </> : null}
  </>
}

type ContinueButtonProps = {
  saving: boolean
  onContinue: () => void
}

const ContinueButton = ({ saving, onContinue }: ContinueButtonProps) => (
  <div>
    <button
      type="button"
      className="btn btn-primary"
      disabled={saving}
      onClick={onContinue}
    >{saving ? <Spinner /> : 'Continue'}</button>
  </div>
)

export const Coborrower = ({ schema, formData, saving, update, save, onError }: SubformProps) => {
  const [validation, setValidation] = useState<Record<string, string>>({})
  const { tenant, registration: { installer } } = useAppState()

  const coRequired = (
    formData.prequalResultCombined === 'pass' &&
    formData.prequalResult !== 'pass'
  )
  const coBorrowerIncluded = coRequired || formData.includeCoBorrower === 'yes'
  const usesPersona =
    formData.identityVerificationVersion === 'persona' &&
    !tenant.config?.personaDisabled
  const coBorrowerVerified =
    !usesPersona ||
    isPersonaVerificationComplete(formData.personaVerifications?.coBorrower)
  const canContinue = !coBorrowerIncluded || coBorrowerVerified

  const validate = () => {
    const fields = [
      'coFirstName',
      'coLastName',
      'coSsn',
      'coPhone',
      'coEmail',
      'coBirthDate',
      'coDriversLicenseNo',
      'coDriversLicenseIssueDate',
      'coDriversLicenseIssuingState',
      'coDriversLicenseExpirationDate',
      'coCitizenship',
      'coBorrowerAddress',
      'coOccupation',
      'coEmployerName',
      'coStatedGrossAnnualIncome',
      'coYearsAtEmployer'
    ]

    if (formData.includeCoBorrower !== 'yes') {
      return {}
    }

    const results = Validate.validateFields(schema, formData, fields)

    if (!installer?.isSupport) {
      const emailMatch = Validate.coborrowerEmailMatchesPrimary(formData.coEmail, formData.email)
      if (emailMatch) {
        results.coEmail = emailMatch
      }

      const phoneMatch = Validate.coborrowerPhoneMatchesPrimary(formData.coPhone, formData.phone)
      if (phoneMatch) {
        results.coPhone = phoneMatch
      }
    }

    if (!['yes', 'no'].includes(formData.coHasMortgage)) {
      results['hasMortgage'] = 'Please indicate whether or not coborrower has a mortgage'
    }

    if (Number(formData.coYearsAtEmployer) < 2 && !formData.coPreviousEmployment) {
      results['previousEmployment'] = 'Please add previous employment'
    }

    return results
  }

  // Persona texts the verification link to the co-borrower phone stored on the loan
  // application, so the form has to be persisted before the verification request.
  const persistBeforeVerify = async (): Promise<boolean> => {
    const results = Validate.validateFields(schema, formData, CO_IDENTITY_FIELDS)
    if (!installer?.isSupport) {
      const emailMatch = Validate.coborrowerEmailMatchesPrimary(formData.coEmail, formData.email)
      if (emailMatch) {
        results.coEmail = emailMatch
      }

      const phoneMatch = Validate.coborrowerPhoneMatchesPrimary(formData.coPhone, formData.phone)
      if (phoneMatch) {
        results.coPhone = phoneMatch
      }
    }
    setValidation(results)

    if (Object.keys(results).length > 0) {
      return false
    }

    let persisted = true
    await save({
      advance: false,
      onCatch: ex => {
        persisted = false
        onError(errorToString(ex))
      },
    })

    return persisted
  }

  return <>
    <CoborrowerFields
      formData={formData}
      update={update}
      onBeforeVerify={persistBeforeVerify}
    />

    {canContinue ? (
      <ContinueButton
        saving={saving}
        onContinue={() => {
          const v = validate()
          setValidation(v)
          if (Object.keys(v).length === 0) {
            save()
          }
        }}
      />
    ) : null}
    {validation ? (
      <Validation validation={validation} /> || <div className="alert alert-info">Form is valid</div>
    ) : null}
  </>
}

export default Coborrower
